import { useEffect, useId, useState } from "react";
import { Input } from "@/design-system/components/ui";
import { researchCopy } from "./researchCopy";
import { moneyInputValue, parseMoneyInput } from "./researchFormat";

/**
 * A money range — a minimum and a maximum — for the research list's "More
 * filters" panel.
 *
 * <p>🔴 <b>TEXT + `inputMode="decimal"`, NEVER `type="number"`</b>, the same
 * choice the brief form's money inputs make (`ResearchBriefForm`): a number
 * input reports `""` for both "cleared" and "malformed", which makes an unset
 * bound and a mistyped one the same thing. Here a mistyped bound is kept on
 * screen, marked invalid, and NOT sent — the list keeps the last good value
 * rather than quietly dropping the filter the reader thought they set.</p>
 *
 * <p>The text is local; the VALUE is the filter's. The local text resyncs only
 * when the value moves out from under it (the brief button, a removed chip, a
 * reset), and a value equal to what the text already parses to leaves the text
 * alone — so "150,000" keeps its comma while the URL holds 150000.</p>
 */
export function MoneyRangeField({
  label,
  min,
  max,
  onMinChange,
  onMaxChange,
  testId,
}: {
  label: string;
  min: number | null;
  max: number | null;
  onMinChange: (value: number | null) => void;
  onMaxChange: (value: number | null) => void;
  testId: string;
}) {
  const legendId = useId();
  return (
    <fieldset className="m-0 grid gap-space-1 border-0 p-0" aria-labelledby={legendId} data-testid={testId}>
      <span id={legendId} className="text-caption uppercase tracking-wide text-gray-mid">
        {label}
      </span>
      <div className="grid grid-cols-2 gap-space-2">
        <MoneyBoundInput
          value={min}
          onChange={onMinChange}
          placeholder={researchCopy.results.moneyMin}
          ariaLabel={researchCopy.results.moneyBoundLabel(label, "min")}
          testId={`${testId}-min`}
        />
        <MoneyBoundInput
          value={max}
          onChange={onMaxChange}
          placeholder={researchCopy.results.moneyMax}
          ariaLabel={researchCopy.results.moneyBoundLabel(label, "max")}
          testId={`${testId}-max`}
        />
      </div>
    </fieldset>
  );
}

function MoneyBoundInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  testId,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder: string;
  ariaLabel: string;
  testId: string;
}) {
  const [text, setText] = useState(() => moneyInputValue(value));

  // Resync only when the VALUE moved and the text does not already say it.
  // Keyed on `value` alone: an invalid entry never calls `onChange`, so it
  // never moves the value, so it is never overwritten while being typed.
  useEffect(() => {
    setText((current) => (parseMoneyInput(current) === value ? current : moneyInputValue(value)));
  }, [value]);

  const parsed = parseMoneyInput(text);
  const invalid = parsed === undefined;

  return (
    <div className="grid gap-[2px]">
      <Input
        type="text"
        inputMode="decimal"
        value={text}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          const n = parseMoneyInput(next);
          if (n !== undefined) onChange(n);
        }}
        placeholder={placeholder}
        invalid={invalid}
        aria-label={ariaLabel}
        data-testid={testId}
      />
      {invalid && (
        <span className="text-caption text-status-error">{researchCopy.results.moneyInvalid}</span>
      )}
    </div>
  );
}
